/**
 * One vault (Settings → Vaults → a vault): who it is (a name people chose, what it receives, who owns it), the
 * machine it runs on, how loaded that machine is right now and in the last hour, what it holds, and a place for
 * notes between colleagues. Everything is read from the Cloud (the vault reports its machine every few seconds);
 * the page polls and keeps what it shows while refreshing. Owners edit in place: the title, the description, the
 * address; viewers read. Pure wording and arithmetic live in lib/vault.ts.
 */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { useFormDraft, useUrlFlag } from '../lib/formstate.ts';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type VaultDetailData, type VaultNote } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { useAsync } from '../lib/hooks.ts';
import { ago, bytes, count, dateTime } from '../lib/format.ts';
import {
  authorName, diskUse, loadTone, memoryFree, orderAddresses, parseVaultName, percent, series, sparkPoints, uptimeNow, uptimeWords, type LoadTone,
} from '../lib/vault.ts';
import { IconButton, Kicker, Pill, Surface, cx } from '../design/primitives.tsx';
import { AlertIcon, CheckIcon, ChevronIcon, CloseIcon, CopyIcon, PencilIcon, PlusIcon, TrashIcon } from '../components/icons.tsx';
import { EnvLabel } from '../components/EnvBits.tsx';
import { Button, Spinner, inputClass, useToast } from '../components/ui.tsx';
import { Confirm } from './Settings.tsx';
import { IngestAddress } from './Connectors.tsx';
import { lastUsed, vaultKeyScope } from '../lib/connectors.ts';
import { configAttention } from '../lib/vaultconfig.ts';
import {
  POLL_EVERY_MS, POLL_FOR_MS, acmeStateView, canRenew, directoryInfo, expiryTone, expiryWords, issuerWords, keepPolling, relativeWords, shortFingerprint, toneWord, type AcmeInfo, type Tone,
} from '../lib/certificate.ts';
import { ConfigTab, useRestart } from './VaultConfig.tsx';
import { LogsTab } from './VaultLogs.tsx';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const POLL_MS = 20_000;
type Tab = 'overview' | 'config' | 'logs';

export function VaultDetail() {
  const { vid: nid = '' } = useParams();
  const { tenant } = useAuth();
  const { link } = useEnv();
  const { canManage } = useAuth();
  const [params, setParams] = useSearchParams();
  const tid = tenant?.id ?? '';
  const [fast, setFast] = useState(false);
  // After "Renew now": poll every 3 s for up to a minute (or until the order has finished)
  const [certStart, setCertStart] = useState<number | null>(null);
  const sawRunning = useRef(false);
  const vault = useAsync(() => Api.vault(tid, nid), [tid, nid], certStart !== null ? POLL_EVERY_MS : fast ? 2000 : POLL_MS);
  const v = vault.data?.id === nid ? vault.data : undefined;
  const acme = v?.tls?.acme;
  useEffect(() => {
    if (certStart === null) return;
    const t = setTimeout(() => setCertStart(null), Math.max(0, POLL_FOR_MS - (Date.now() - certStart)));
    return () => clearTimeout(t);
  }, [certStart]);
  useEffect(() => {
    if (certStart === null) return;
    if (acmeStateView(acme?.state).running) sawRunning.current = true;
    if (!keepPolling(certStart, Date.now(), acme, sawRunning.current)) setCertStart(null);
  }, [acme, certStart]);
  const restart = useRestart(tid, v, vault.reload);
  useEffect(() => setFast(restart.state.kind === 'waiting'), [restart.state.kind]);
  // Configuration and Logs are for owners (the Cloud answers viewers with 403); everyone else sees the overview only
  const tab: Tab = canManage && (params.get('tab') === 'config' || params.get('tab') === 'logs') ? (params.get('tab') as Tab) : 'overview';
  const goto = (t: Tab) => setParams(t === 'overview' ? {} : { tab: t });

  return (
    <div className="flex flex-col gap-6 w-full">
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-small text-n8 min-w-0">
        <Link to={link('/settings/vaults')} className="text-n8 hover:text-n10 no-underline shrink-0">Vaults</Link>
        <span className="text-n6" aria-hidden="true">/</span>
        <span className="text-n10 truncate" aria-current="page">{v?.name ?? '…'}</span>
      </nav>
      {vault.error && !v && <Surface className="p-8 text-center text-small text-n8">{vault.error === 'Vault not found' || /not found/i.test(vault.error) ? 'This vault does not exist (any more).' : vault.error}</Surface>}
      {!v && !vault.error && <Spinner />}
      {v && (
        <>
          <Header v={v} tid={tid} onChanged={vault.reload} onConfig={() => goto('config')} />
          {canManage && (
            <div className="inline-flex self-start bg-n3 rounded-full p-0.5 max-w-full" role="group" aria-label="Vault sections">
              <Pill active={tab === 'overview'} onClick={() => goto('overview')}>Overview</Pill>
              <Pill active={tab === 'config'} onClick={() => goto('config')}>Configuration</Pill>
              <Pill active={tab === 'logs'} onClick={() => goto('logs')}>Logs</Pill>
            </div>
          )}
          {tab === 'overview' && (
            <>
              <Description v={v} tid={tid} onChanged={vault.reload} />
              <div className="grid gap-6 lg:grid-cols-2 items-start">
                <Server v={v} />
                <Resources v={v} />
              </div>
              {v.tls && <CertificateCard v={v} tid={tid} polling={certStart !== null} onRenewed={() => { sawRunning.current = false; setCertStart(Date.now()); vault.reload(); }} />}
              <DataCard v={v} tid={tid} onChanged={vault.reload} />
              <SendingKeys v={v} />
              <Notes v={v} tid={tid} onChanged={vault.reload} />
            </>
          )}
          {tab === 'config' && <ConfigTab v={v} tid={tid} restart={restart} />}
          {tab === 'logs' && <LogsTab v={v} tid={tid} />}
        </>
      )}
    </div>
  );
}

function Card({ title, aside, children, className }: { title: string; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Surface className={cx('p-5 flex flex-col gap-4 min-w-0', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Kicker>{title}</Kicker>
        {aside}
      </div>
      {children}
    </Surface>
  );
}

// ---------------------------------------------------------------- header: the name, the state

function Header({ v, tid, onChanged, onConfig }: { v: VaultDetailData; tid: string; onChanged: () => void; onConfig: () => void }) {
  const { canManage } = useAuth();
  const { envs } = useEnv();
  const toast = useToast();
  // An unsent rename (edit mode and text) is a draft in sessionStorage per vault, so Back returns to it
  const [{ editing, value }, setDraft, clearDraft] = useFormDraft(`vault.name.${v.id}`, { editing: false, value: '' });
  const restored = useRef(editing);
  const setEditing = (on: boolean) => { if (on) setDraft({ editing: true }); else clearDraft(); };
  const setValue = (t: string) => setDraft({ value: t });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const env = envs.find((e) => e.id === v.environmentId);
  const custom = v.name !== v.reportedName;
  const attention = configAttention(v.config);

  const save = async (name: string | null) => {
    setBusy(true);
    try {
      await Api.updateVault(tid, v.id, { name });
      toast(name ? 'Name saved' : 'Name reset');
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const submit = () => {
    const r = parseVaultName(value);
    if ('error' in r) return setError(r.error);
    if ((r.name ?? v.reportedName) === v.name) return setEditing(false);
    void save(r.name);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setError(undefined); }
  };
  const start = () => { restored.current = false; setDraft({ value: v.name, editing: true }); setError(undefined); };

  return (
    <header className="flex flex-col gap-2 min-w-0">
      {editing ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <input aria-label="Vault name" aria-invalid={!!error} className={cx(inputClass, 'h-11 text-title font-semibold tracking-tight max-w-xl')} value={value} autoFocus maxLength={90}
              onFocus={(e) => { if (!restored.current) e.currentTarget.select(); restored.current = false; }} onChange={(e) => { setValue(e.target.value); setError(undefined); }} onKeyDown={onKey} />
            <IconButton label="Save name" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={submit}><CheckIcon size={18} /></IconButton>
            <IconButton label="Cancel" onMouseDown={(e) => e.preventDefault()} onClick={() => { setEditing(false); setError(undefined); }}><CloseIcon size={18} /></IconButton>
          </div>
          <p className={cx('m-0 text-micro', error ? 'text-fail' : 'text-n7')}>{error ?? 'Enter saves, Esc cancels. Empty goes back to the name the vault reports.'}</p>
        </div>
      ) : (
        <div className="flex items-center gap-2 min-w-0 group">
          <h1 className="m-0 text-title font-semibold tracking-tight truncate">{v.name}</h1>
          {canManage && (
            <IconButton label="Rename vault" onClick={start} className="shrink-0"><PencilIcon size={16} /></IconButton>
          )}
        </div>
      )}
      {custom && !editing && (
        <p className="m-0 text-small text-n7 flex items-center gap-2 flex-wrap">
          reported as <span className="font-mono text-micro text-n8">{v.reportedName}</span>
          {canManage && <Button variant="ghost" className="h-7 px-2 text-micro" disabled={busy} onClick={() => void save(null)}>Reset name</Button>}
        </p>
      )}
      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap text-small text-n9">
        <span className="inline-flex items-center gap-2">
          <span className={cx('size-2 rounded-full shrink-0', v.online ? 'bg-ok' : 'bg-n6')} aria-hidden="true" />
          {v.online ? (v.connectedSince ? `Connected since ${dateTime(v.connectedSince)}` : 'Connected') : `Offline, seen ${ago(v.lastSeen)}`}
        </span>
        {v.version && <span className="text-n8">version {v.version}</span>}
        <span className="inline-flex items-center rounded-full bg-n3 h-6 px-2.5 text-micro text-n8"><EnvLabel env={env} /></span>
        {canManage && attention && (
          <button type="button" onClick={onConfig} className="inline-flex items-center gap-2 rounded-full bg-n3 hover:bg-n4 h-6 px-2.5 text-micro text-n9 border-0 cursor-pointer focus-visible:ring-[1.5px] focus-visible:ring-accent">
            <i className={cx('size-2 rounded-full shrink-0', attention.tone === 'fail' ? 'bg-fail' : 'bg-warn')} aria-hidden="true" />{attention.label}
          </button>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------- keys that send here

function SendingKeys({ v }: { v: VaultDetailData }) {
  const { link } = useEnv();
  const keys = v.connectorKeys ?? [];
  return (
    <Card title="Keys that send here" aside={keys.length > 0 ? <span className="text-micro text-n7">{keys.length === 1 ? '1 key' : `${keys.length} keys`}</span> : undefined}>
      {keys.length === 0 ? (
        <p className="m-0 text-small text-n8">
          No connector key reaches this vault yet. Create one under <Link to={link('/settings/connectors')} className="text-accent no-underline hover:underline">Connectors</Link>.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col">
          {keys.map((k, i) => (
            <li key={k.keyId} className={cx('py-2.5 flex items-center gap-x-4 gap-y-1 flex-wrap', i > 0 && 'border-t border-n4')}>
              <div className="flex-1 min-w-[180px] flex flex-col gap-0.5">
                <span className="text-small font-medium">{k.name}</span>
                <code className="font-mono text-micro text-n7 [overflow-wrap:anywhere]">{k.keyId}</code>
              </div>
              <span className="text-micro text-n8 rounded-full bg-n3 h-6 px-2.5 inline-flex items-center">{vaultKeyScope(k.scope)}</span>
              <span className="text-micro text-n7 sm:text-right min-w-[84px]">{k.lastUsedAt ? `used ${lastUsed(k.lastUsedAt)}` : 'not used yet'}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- description

function Description({ v, tid, onChanged }: { v: VaultDetailData; tid: string; onChanged: () => void }) {
  const { canManage } = useAuth();
  const toast = useToast();
  // An unsent description (edit mode and text) is a draft in sessionStorage per vault, so Back returns to it
  const [{ editing, value }, setDraft, clearDraft] = useFormDraft(`vault.description.${v.id}`, { editing: false, value: '' });
  const setEditing = (on: boolean) => { if (on) setDraft({ editing: true }); else clearDraft(); };
  const setValue = (t: string) => setDraft({ value: t });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const start = () => { setDraft({ value: v.description ?? '', editing: true }); setError(undefined); };
  const save = async () => {
    setBusy(true);
    try {
      await Api.updateVault(tid, v.id, { description: value.trim() ? value : null });
      toast('Description saved');
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
    else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); }
  };

  if (editing) {
    return (
      <Card title="Description">
        <textarea aria-label="Description" autoFocus rows={4} maxLength={2000} value={value} onKeyDown={onKey} onChange={(e) => { setValue(e.target.value); setError(undefined); }}
          placeholder="What it receives, who owns it"
          className="w-full rounded-chip bg-n3 px-3 py-2.5 text-body text-n10 border-0 outline-none resize-y focus-visible:ring-[1.5px] focus-visible:ring-accent placeholder:text-n7" />
        {error && <p className="m-0 text-small text-fail">{error}</p>}
        <div className="flex items-center gap-2">
          <Button variant="primary" disabled={busy} onClick={() => void save()}>Save</Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          <span className="ml-auto text-micro text-n7 tabular-nums">{value.length} / 2000</span>
        </div>
      </Card>
    );
  }
  if (!v.description) {
    if (!canManage) return null;
    return (
      <button type="button" onClick={start} className="text-left bg-transparent border border-dashed border-n5 hover:border-n7 hover:bg-n2/60 rounded-card px-5 py-4 text-body text-n8 cursor-pointer transition-colors duration-[var(--t-micro)]">
        Add a description: what it receives, who owns it
      </button>
    );
  }
  return (
    <Card title="Description" aside={canManage && <IconButton label="Edit description" onClick={start} className="-my-1.5"><PencilIcon size={16} /></IconButton>}>
      <p className="m-0 text-body text-n10 whitespace-pre-wrap [overflow-wrap:anywhere] max-w-[68ch]">{v.description}</p>
    </Card>
  );
}

// ---------------------------------------------------------------- server

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-baseline py-2.5 border-t border-n4 first:border-t-0 first:pt-0 last:pb-0">
      <dt className="text-small text-n8">{label}</dt>
      <dd className="m-0 text-small text-n10 min-w-0 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function CopyText({ text, shown, className }: { text: string; shown?: string; className?: string }) {
  const toast = useToast();
  return (
    <button type="button" title={shown ? `${text}\nClick to copy` : 'Copy'} aria-label={shown ? `Copy ${text}` : undefined} onClick={() => { void navigator.clipboard?.writeText(text); toast('Copied'); }}
      className={cx('inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-inherit cursor-pointer text-left rounded-chip hover:text-accent focus-visible:ring-[1.5px] focus-visible:ring-accent group', className)}>
      <span className="[overflow-wrap:anywhere]">{shown ?? text}</span>
      <CopyIcon size={13} className="shrink-0 opacity-0 group-hover:opacity-70 group-focus-visible:opacity-70" />
    </button>
  );
}

function Server({ v }: { v: VaultDetailData }) {
  const h = v.host;
  const [all, setAll] = useUrlFlag('addr');
  const addr = orderAddresses(h?.addresses);
  const up = uptimeNow(v.status, v.lastSeen);
  return (
    <Card title="Server">
      <dl className="m-0 flex flex-col">
        <Fact label="Host name">{h?.hostname ?? v.hostname ?? '—'}</Fact>
        {h && (
          <Fact label="Addresses">
            {addr.shown.length + addr.more.length === 0 ? '—' : (
              <span className="flex flex-col gap-1 items-start">
                {[...addr.shown, ...(all ? addr.more : [])].map((a) => (
                  <span key={a.address} className="inline-flex items-baseline gap-2">
                    <CopyText text={a.address} className="font-mono text-micro text-n10" />
                    <span className="text-micro text-n7">{a.name}</span>
                  </span>
                ))}
                {addr.more.length > 0 && (
                  <button type="button" aria-expanded={all} onClick={() => setAll(!all)} className="inline-flex items-center gap-1 bg-transparent border-0 p-0 text-micro text-n8 hover:text-n10 cursor-pointer">
                    {all ? 'show fewer' : `+${addr.more.length} more`}<ChevronIcon size={12} className={all ? 'rotate-180' : ''} />
                  </button>
                )}
              </span>
            )}
          </Fact>
        )}
        <Fact label="System">{[h?.os ?? v.os, h?.osVersion, h?.arch].filter(Boolean).join(' ') || '—'}</Fact>
        {h && <Fact label="CPU cores">{h.cpus ?? '—'}</Fact>}
        {h && <Fact label="Java">{h.java ?? '—'}</Fact>}
        {h && <Fact label="Process id"><span className="font-mono text-micro">{h.pid ?? '—'}</span></Fact>}
        <Fact label="Uptime">{v.online ? uptimeWords(up) : <span className="text-n8">offline</span>}</Fact>
        {h?.dataDir && <Fact label="Data directory"><CopyText text={h.dataDir} className="font-mono text-micro" /></Fact>}
        <Fact label="Vault id"><span className="font-mono text-micro text-n8">{v.id}</span></Fact>
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------- resources

const TONE_TEXT: Record<LoadTone, string> = { ok: '', warn: 'text-warn', fail: 'text-fail' };

function Meter({ label, value, sub, frac, tone, toneWord, children, segments }: {
  label: string; value: string; sub: ReactNode; frac: number | null; tone: LoadTone; toneWord?: string; children?: ReactNode;
  /** A marked part of the bar from the left (the vault's own data), as a fraction of the whole. */
  segments?: number | null;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-small text-n8">{label}</span>
        {tone !== 'ok' && (
          <span className={cx('inline-flex items-center gap-1.5 text-small font-medium', TONE_TEXT[tone])} role="status">
            <AlertIcon size={14} />{toneWord}
          </span>
        )}
      </div>
      <div className="text-title font-semibold tabular-nums leading-none">{value}</div>
      {frac !== null && (
        <div className="relative h-2 rounded-full bg-n4 overflow-hidden" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(frac * 100)}>
          <div className={cx('absolute inset-y-0 left-0 rounded-full', tone === 'ok' ? 'bg-n7' : tone === 'warn' ? 'bg-warn' : 'bg-fail')} style={{ width: `${Math.max(frac > 0 ? 1 : 0, frac * 100)}%` }} />
          {segments != null && segments > 0 && <div className="absolute inset-y-0 left-0 rounded-full bg-accent ring-1 ring-n2" style={{ width: `${Math.max(1.5, segments * 100)}%` }} />}
        </div>
      )}
      <div className="text-micro text-n7 flex flex-col gap-1">{sub}</div>
      {children}
    </div>
  );
}

function Spark({ values, label }: { values: number[]; label: string }) {
  const W = 300, H = 36;
  if (values.length < 2) return <p className="m-0 text-micro text-n7">The last hour fills in as the vault reports.</p>;
  const pts = sparkPoints(values, W, H);
  return (
    <figure className="m-0 flex flex-col gap-1">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-9 text-accent block" role="img" aria-label={`${label}: ${percent(values[values.length - 1])} now, ${percent(Math.min(...values))} to ${percent(Math.max(...values))} over the last hour`}>
        <polygon points={`0,${H} ${pts} ${W},${H}`} fill="currentColor" opacity="0.1" />
        <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
      <figcaption className="text-micro text-n7 flex justify-between"><span>1 h ago</span><span>now</span></figcaption>
    </figure>
  );
}

function Resources({ v }: { v: VaultDetailData }) {
  const h = v.host;
  const bytesOnDisk = typeof v.status?.bytesOnDisk === 'number' ? v.status.bytesOnDisk : null;
  const cpu = h?.cpuSystem ?? null;
  const disk = diskUse(h, bytesOnDisk);
  const mem = memoryFree(h);
  return (
    <Card title="Resources" aside={v.online && h ? <span className="text-micro text-n7">live</span> : undefined}>
      {!h ? (
        <p className="m-0 text-small text-n8 max-w-[44ch]">This vault does not report resource data yet. Update the vault to the latest version to see its server, CPU, memory and disk here.</p>
      ) : (
        <div className="flex flex-col gap-6">
          <Meter label="CPU" value={percent(cpu)} frac={cpu} tone={loadTone('cpu', cpu)} toneWord="High"
            sub={<span>system load now{h.load1 !== null ? ` · load 1 min ${h.load1.toFixed(2)}` : ''}{h.cpus ? ` on ${h.cpus} cores` : ''}{h.cpuProcess !== null ? ` · this vault ${percent(h.cpuProcess)}` : ''}</span>}>
            <Spark values={series(v.history, 'cpuSystem')} label="CPU" />
          </Meter>
          {mem ? (
            <Meter label="Memory" value={`${bytes(mem.free)} free`} frac={1 - mem.freeFrac} tone="ok"
              sub={<span>free of {bytes(mem.total)}. The system counts its cache as used, so this is usually low.</span>} />
          ) : <Meter label="Memory" value="—" frac={null} tone="ok" sub="not reported" />}
          {disk ? (
            <Meter label="Disk" value={`${percent(disk.usedFrac)} used`} frac={disk.usedFrac} segments={disk.dataFrac} tone={loadTone('disk', disk.usedFrac)} toneWord={disk.usedFrac >= 0.95 ? 'Almost full' : 'Filling up'}
              sub={
                <>
                  <span>{bytes(disk.used)} used · {bytes(disk.free)} free · {bytes(disk.total)} total</span>
                  {disk.dataFrac !== null && bytesOnDisk !== null && (
                    <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-full bg-accent inline-block" aria-hidden="true" />this vault's data {bytes(bytesOnDisk)}</span>
                  )}
                </>
              } />
          ) : <Meter label="Disk" value="—" frac={null} tone="ok" sub="not reported" />}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- certificate

const TONE_CLASS: Record<Tone, string> = { ok: 'text-n9', warn: 'text-warn', fail: 'text-fail' };

/** Status chip with the tone as icon and word, never colour alone. */
function ToneChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 rounded-full bg-n3 h-6 px-2.5 text-micro whitespace-nowrap', TONE_CLASS[tone])}>
      {tone === 'ok' ? <CheckIcon size={13} /> : <AlertIcon size={13} />}{children}
    </span>
  );
}

function CertificateCard({ v, tid, polling, onRenewed }: { v: VaultDetailData; tid: string; polling: boolean; onRenewed: () => void }) {
  const tls = v.tls!;
  const { canManage } = useAuth();
  const hasCert = typeof tls.notAfter === 'number';
  const tone = hasCert ? expiryTone(tls.notAfter) : 'ok';
  const acme = tls.acme?.enabled ? tls.acme : undefined;
  return (
    <Card title="Certificate" aside={hasCert ? <ToneChip tone={tone}>{toneWord(tone)}</ToneChip> : undefined}>
      {hasCert ? (
        <dl className="m-0 flex flex-col">
          <Fact label="Subject">{tls.subject ?? '—'}</Fact>
          <Fact label="Issuer">{issuerWords(tls.selfSigned)}{tls.selfSigned === true && <span className="text-n7"> · senders must trust it by its fingerprint</span>}</Fact>
          <Fact label="Expires">
            <span className="tabular-nums">{dateTime(tls.notAfter)}</span>
            <span className={cx('ml-2', tone === 'ok' ? 'text-n8' : TONE_CLASS[tone] + ' font-medium')}>{expiryWords(tls.notAfter!)}</span>
          </Fact>
          {tls.fingerprintSha256 && (
            <Fact label="Fingerprint">
              <CopyText text={tls.fingerprintSha256} shown={shortFingerprint(tls.fingerprintSha256)} className="font-mono text-micro" />
            </Fact>
          )}
        </dl>
      ) : (
        <p className="m-0 text-small text-n8">No certificate yet{acme ? ': the Vault is getting its first one.' : '.'}</p>
      )}
      {acme ? (
        <AcmeBlock acme={acme} online={v.online} canManage={canManage} tid={tid} vaultId={v.id} polling={polling} onRenewed={onRenewed} />
      ) : (
        canManage && (
          <p className="m-0 border-t border-n4 pt-4 text-small text-n8">
            Let the Vault manage its own certificate: enable <Link to="?tab=config" className="font-mono text-micro text-accent no-underline hover:underline">tls.acme</Link> in the Configuration tab.
          </p>
        )
      )}
    </Card>
  );
}

function AcmeBlock({ acme, online, canManage, tid, vaultId, polling, onRenewed }: {
  acme: AcmeInfo; online: boolean; canManage: boolean; tid: string; vaultId: string; polling: boolean; onRenewed: () => void;
}) {
  const toast = useToast();
  const [asking, setAsking] = useUrlFlag('renew');
  const [busy, setBusy] = useState(false);
  const st = acmeStateView(acme.state);
  const dir = directoryInfo(acme.directory);
  const renew = async () => {
    setAsking(false);
    setBusy(true);
    try {
      await Api.renewCertificate(tid, vaultId);
      toast('Renewal started');
      onRenewed();
    } catch (e) {
      toast(message(e));
    } finally {
      setBusy(false);
    }
  };
  const chipTone: Tone = st.tone === 'fail' ? 'fail' : 'ok';
  return (
    <div className="border-t border-n4 pt-4 flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-small font-semibold">Managed certificate (ACME)</span>
        <span className={cx('inline-flex items-center gap-1.5 rounded-full bg-n3 h-6 px-2.5 text-micro whitespace-nowrap', TONE_CLASS[chipTone])} data-acme-state={acme.state}>
          {st.tone === 'busy' ? <Spinner className="size-3.5" /> : st.tone === 'ok' ? <CheckIcon size={13} /> : st.tone === 'fail' ? <AlertIcon size={13} /> : <i className="size-2 rounded-full bg-n6" aria-hidden="true" />}
          {st.label}
        </span>
      </div>
      <dl className="m-0 flex flex-col">
        <Fact label="Challenge"><span className="font-mono text-micro">{acme.challenge ?? '—'}</span></Fact>
        <Fact label="Domain"><span className="font-mono text-micro">{acme.domain ?? '—'}</span></Fact>
        <Fact label="Directory">
          <span className={dir.label.startsWith('http') ? 'font-mono text-micro' : ''}>{dir.label}</span>
          {dir.testCa && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-n3 h-5 px-2 text-micro text-warn"><AlertIcon size={12} />test CA: browsers do not trust it</span>}
        </Fact>
        <Fact label="Last issued">{acme.lastIssuedAt ? <><span className="tabular-nums">{dateTime(acme.lastIssuedAt)}</span><span className="text-n8"> · {relativeWords(acme.lastIssuedAt)}</span></> : 'never'}</Fact>
        <Fact label="Next renewal">{acme.nextRenewalAt ? <><span className="tabular-nums">{dateTime(acme.nextRenewalAt)}</span><span className="text-n8"> · {relativeWords(acme.nextRenewalAt)}</span></> : '—'}</Fact>
        {acme.lastError && (
          <Fact label="Last error">
            <span className="text-fail inline-flex items-start gap-1.5"><AlertIcon size={14} className="mt-0.5 shrink-0" /><span>{acme.lastError.message}</span></span>
            <div className="text-micro text-n7 mt-0.5">{dateTime(acme.lastError.at)} · {relativeWords(acme.lastError.at)}</div>
          </Fact>
        )}
      </dl>
      {canManage && (asking ? (
        <Confirm question="Renew the certificate now?" action="Renew now" onCancel={() => setAsking(false)} onConfirm={() => void renew()}>
          The Vault orders a new certificate from the CA right away. The current one stays in use until the new one is installed.
        </Confirm>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <Button disabled={!canRenew(acme, online) || busy} onClick={() => setAsking(true)}>{busy ? <Spinner className="size-4" /> : null}Renew now</Button>
          {!online ? <span className="text-micro text-n7">The Vault is offline.</span>
            : st.running ? <span className="text-micro text-n7">An order is running{polling ? ', this page follows it' : ''}.</span> : null}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- data

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-small text-n8">{label}</span>
      <span className="text-title font-semibold tabular-nums leading-tight">{value}</span>
      {sub && <span className="text-micro text-n7">{sub}</span>}
    </div>
  );
}

function DataCard({ v, tid, onChanged }: { v: VaultDetailData; tid: string; onChanged: () => void }) {
  const s = v.status;
  const days = (s?.partitions ?? []).map((p) => p.day).sort();
  return (
    <Card title="Data">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
        <Stat label="Documents" value={count(s?.messages)} />
        <Stat label="Data days" value={s?.partitions ? days.length : '—'} sub={days.length ? `${days[0]} to ${days[days.length - 1]}` : undefined} />
        <Stat label="Message classes" value={count(typeof s?.messageClasses === 'number' ? s.messageClasses : null)} />
        <Stat label="On disk" value={bytes(s?.bytesOnDisk)} />
      </div>
      <div className="border-t border-n4 pt-4 flex flex-col gap-2">
        <span className="text-small text-n8">Address connectors send to</span>
        <IngestAddress vault={v} tid={tid} onSaved={onChanged} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- notes

function Notes({ v, tid, onChanged }: { v: VaultDetailData; tid: string; onChanged: () => void }) {
  const { me, canManage } = useAuth();
  const toast = useToast();
  const notes = useAsync(() => Api.vaultNotes(tid, v.id), [tid, v.id], POLL_MS);
  // The unsent note is a draft in sessionStorage per vault, so Back returns to it
  const [{ text }, setDraft, clearDraft] = useFormDraft(`vault.note.${v.id}`, { text: '' });
  const setText = (t: string) => setDraft({ text: t });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [deleting, setDeleting] = useState<VaultNote>();
  const email = me?.user.email ?? null;
  const area = useRef<HTMLTextAreaElement>(null);
  // Once the count on the vault changes (a colleague wrote), the list follows at once
  const seen = useRef(v.noteCount);
  useEffect(() => { if (seen.current !== v.noteCount) { seen.current = v.noteCount; notes.reload(); } }, [v.noteCount]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = notes.data ?? [];

  const add = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await Api.addVaultNote(tid, v.id, text.trim());
      clearDraft();
      notes.reload();
      onChanged();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Notes" aside={<span className="text-micro text-n7 tabular-nums">{list.length > 0 ? list.length : ''}</span>}>
      {canManage && (
        <form onSubmit={(e) => void add(e)} className="flex flex-col gap-2">
          <textarea ref={area} aria-label="New note" rows={2} maxLength={2000} value={text} placeholder="Leave a note: a change, a decision, something the next person should know"
            onChange={(e) => { setText(e.target.value); setError(undefined); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void add(); } }}
            className="w-full rounded-chip bg-n3 px-3 py-2.5 text-small text-n10 border-0 outline-none resize-y focus-visible:ring-[1.5px] focus-visible:ring-accent placeholder:text-n7" />
          {error && <p className="m-0 text-small text-fail">{error}</p>}
          <div className="flex items-center gap-3">
            <Button type="submit" variant="primary" disabled={busy || !text.trim()}><PlusIcon size={16} />Add note</Button>
            {text.length > 1500 && <span className="text-micro text-n7 tabular-nums">{text.length} / 2000</span>}
          </div>
        </form>
      )}
      {deleting && (
        <Confirm question="Delete this note?" action="Delete" onCancel={() => setDeleting(undefined)} onConfirm={async () => {
          try {
            await Api.deleteVaultNote(tid, v.id, deleting.id);
            toast('Note deleted');
            notes.reload();
            onChanged();
          } catch (err) {
            toast(message(err), 'fail');
          }
          setDeleting(undefined);
        }}>
          {authorName(deleting.user)}'s note from {dateTime(deleting.at)} is removed for everyone.
        </Confirm>
      )}
      {notes.error && !notes.data && <p className="m-0 text-small text-fail">{notes.error}</p>}
      {notes.loading && !notes.data && <Spinner />}
      {notes.data && list.length === 0 && <p className="m-0 text-small text-n8">{canManage ? 'No notes yet.' : 'No notes.'}</p>}
      {list.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col">
          {list.map((n, i) => (
            <li key={n.id} className={cx('py-3 flex gap-3 items-start', i > 0 && 'border-t border-n4', i === 0 && 'pt-1')}>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <div className="flex items-baseline gap-2 text-micro text-n7">
                  <span className="text-small font-medium text-n10">{authorName(n.user)}</span>
                  <time dateTime={new Date(n.at).toISOString()} title={dateTime(n.at)}>{ago(n.at)}</time>
                </div>
                <p className="m-0 text-small text-n10 whitespace-pre-wrap [overflow-wrap:anywhere]">{n.text}</p>
              </div>
              {(canManage || (email !== null && n.user === email)) && (
                <IconButton label={`Delete note by ${authorName(n.user)}`} onClick={() => setDeleting(n)} className="shrink-0 -mt-1"><TrashIcon size={16} /></IconButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
